import { apis } from 'api-service/apis';
import { isObject } from 'lib';
import {
  NATIVE_PLATFORMS,
  mapPlatformToPath,
  removeUrlProtocolParts,
} from 'utils';

/**
 * Native-platform tabs, in the order the design shows them (Bale, Eitaa,
 * Rubika) instead of the aggregated "nativePlatforms" tab.
 */
export const NATIVE_RESOURCE_TABS = ['bale', 'eitaa', 'rubika'];

const NATIVE_PLATFORM_LINKS = {
  eitaa_channel: 'https://eitaa.com',
  bale_channel: 'https://ble.ir',
  rubika_channel: 'https://rubika.ir',
};

export const isNativeResourcePlatform = (platform) =>
  NATIVE_PLATFORMS.includes(platform);

export const expandResourcePlatforms = (platforms, extra = []) => {
  const expanded = platforms.flatMap((platform) =>
    platform === 'nativePlatforms' ? NATIVE_RESOURCE_TABS : platform
  );
  return [
    ...expanded,
    ...extra.filter((platform) => platform && !expanded.includes(platform)),
  ];
};

/**
 * Platform tab order of the category, VIP and blacklist pages, right to left.
 */
const CATEGORY_SEARCH_TAB_ORDER = [
  'news',
  'instagram',
  'twitter',
  'telegram',
  'virasty',
  'linkedin',
  'newspaper',
  'bale',
  'eitaa',
  'rubika',
];

export const orderCategorySearchPlatforms = (platforms) => {
  const available = new Set(platforms);
  const ordered = CATEGORY_SEARCH_TAB_ORDER.filter((platform) =>
    available.has(platform)
  );
  const rest = platforms.filter((platform) => !ordered.includes(platform));
  return [...ordered, ...rest];
};

export const getNativeSearchPlatforms = (platform) =>
  isNativeResourcePlatform(platform) ? [`${platform}_channel`] : undefined;

/**
 * The tag-resources endpoint takes each native tab as its own `platform`
 * value (`bale-channels`) instead of the shared iranian-platforms path.
 */
export const getCategoryNativePath = (platform) =>
  isNativeResourcePlatform(platform) ? `${platform}-channels` : undefined;

export const getNativeChannelKey = (platform) =>
  isNativeResourcePlatform(platform) ? `${platform}_channel` : undefined;

/**
 * Tag-resource responses mix Bale / Eitaa / Rubika under one API path.
 * Match a row to the tab the user is looking at.
 */
export const itemMatchesResourcePlatform = (item, platform) => {
  if (!isNativeResourcePlatform(platform)) return true;
  return (
    item.platform === getNativeChannelKey(platform) ||
    item.platform === platform
  );
};

export const filterItemsByResourcePlatform = (items, platform) =>
  items.filter((item) => itemMatchesResourcePlatform(item, platform));

export const getResourceRoutePlatform = (platform) =>
  isNativeResourcePlatform(platform) ? 'nativePlatforms' : platform;

/**
 * Every metric a source row can show. Add a new metric here, then reference
 * its key in `RESOURCE_PLATFORM_METRICS`.
 */
export const RESOURCE_METRICS = {
  followerCount: {
    labelKey: 'filter:followerCount',
    getValue: (item, platform) => {
      if (platform === 'twitter') {
        return item.public_metrics?.followers_count ?? item.follower_count;
      }
      return item.follower ?? item.follower_count;
    },
  },
  memberCount: {
    labelKey: 'filter:memberCount',
    getValue: (item) => item.member_count,
  },
  influence: {
    labelKey: 'profile:influenceRate.title',
    getValue: (item) => item.influence_category,
    isInfluence: true,
  },
  publishRate: {
    labelKey: 'filter:contentCount',
    getValue: (item) => item.post_count,
  },
};

/**
 * Default columns of a source row: one sortable metric plus influence.
 * Telegram starts on content count, not member count.
 */
export const RESOURCE_PLATFORM_METRICS = {
  news: ['publishRate', 'influence'],
  newspaper: ['publishRate', 'influence'],
  instagram: ['publishRate', 'influence'],
  twitter: ['followerCount', 'influence'],
  virasty: ['followerCount', 'influence'],
  linkedin: ['followerCount', 'influence'],
  telegram: ['publishRate', 'influence'],
  nativePlatforms: ['memberCount', 'influence'],
  bale: ['memberCount', 'influence'],
  eitaa: ['memberCount', 'influence'],
  rubika: ['memberCount', 'influence'],
};

/**
 * Category page columns, right to left.
 * News and newspaper: influence.
 * Instagram and Twitter: content count, followers, influence.
 * Telegram, Bale, Eitaa and Rubika: member count and influence.
 * Virasty and LinkedIn: follower count.
 */
export const CATEGORY_PLATFORM_METRICS = {
  news: ['influence'],
  newspaper: ['influence'],
  instagram: ['publishRate', 'followerCount', 'influence'],
  twitter: ['publishRate', 'followerCount', 'influence'],
  telegram: ['memberCount', 'influence'],
  bale: ['memberCount', 'influence'],
  eitaa: ['memberCount', 'influence'],
  rubika: ['memberCount', 'influence'],
  virasty: ['followerCount'],
  linkedin: ['followerCount'],
};

export const VIP_CARD_ACTIVE = {
  instagram: { background: '#fff6f9', border: '#c63c73' },
  twitter: { background: '#f3fbfe', border: '#5ad1f6' },
  telegram: { background: '#f2fafd', border: '#32aadd' },
};

export const RESOURCE_PLATFORM_ICONS = {
  news: 'ri-global-line',
  newspaper: 'ri-newspaper-line',
  instagram: 'ri-instagram-line',
  twitter: 'ri-twitter-line',
  telegram: 'ri-telegram-line',
  linkedin: 'ri-linkedin-box-line',
  nativePlatforms: 'ri-message-3-line',
};

/**
 * SVG icons of platforms without a Remix icon. `tabIcon` is the unselected
 * icon of the platform tabs, muted like the other tab icons.
 */
export const RESOURCE_PLATFORM_CUSTOM_ICONS = {
  bale: {
    icon: 'bale-line',
    tabIcon: 'bale-line-inactive',
    activeIcon: 'bale-line-active',
  },
  eitaa: {
    icon: 'eitaa-line',
    tabIcon: 'eitaa-line-inactive',
    activeIcon: 'eitaa-line-active',
  },
  rubika: {
    icon: 'rubika-line',
    tabIcon: 'rubika-line-inactive',
    activeIcon: 'rubika-line-active',
  },
  virasty: {
    icon: 'virasty-line',
    tabIcon: 'virasty-line-inactive',
    activeIcon: 'virasty-line-active',
  },
};

const API_PATH_TO_PLATFORM = {
  newsagencies: 'news',
  newspaperagencies: 'newspaper',
  instagramusers: 'instagram',
  twitterusers: 'twitter',
  telegramchannels: 'telegram',
  virastyusers: 'virasty',
  linkedinusers: 'linkedin',
  'iranian-platforms-channels': 'nativePlatforms',
};

const NATIVE_CHANNEL_TO_TAB = {
  bale_channel: 'bale',
  eitaa_channel: 'eitaa',
  rubika_channel: 'rubika',
};

const CATEGORY_PLATFORM_ORDER = [
  'news',
  'instagram',
  'twitter',
  'telegram',
  'newspaper',
  'nativePlatforms',
  'virasty',
  'linkedin',
];

const hasPlatformCount = (value) => Number(value) > 0;

const resolvePlatformKey = (key) =>
  API_PATH_TO_PLATFORM[key] ||
  NATIVE_CHANNEL_TO_TAB[key] ||
  (NATIVE_RESOURCE_TABS.includes(key) ? key : undefined);

const addDisplayPlatform = (visible, key, value) => {
  if (!hasPlatformCount(value)) return;
  const platform = resolvePlatformKey(key);
  if (platform === 'nativePlatforms') {
    NATIVE_RESOURCE_TABS.forEach((tab) => visible.add(tab));
    return;
  }
  if (platform) visible.add(platform);
};

export const getPlatformFromApiPath = (apiPath) =>
  API_PATH_TO_PLATFORM[apiPath];

/**
 * Platforms shown on a category card. Native sources stay split into
 * Bale / Eitaa / Rubika instead of one aggregated "native" mark.
 */
export const getCategoryDisplayPlatforms = (platforms = {}) => {
  const visible = new Set();

  Object.entries(platforms).forEach(([key, value]) => {
    if (isObject(value) && !Array.isArray(value)) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        addDisplayPlatform(visible, nestedKey, nestedValue);
      });
      return;
    }
    addDisplayPlatform(visible, key, value);
  });

  return expandResourcePlatforms(CATEGORY_PLATFORM_ORDER).filter((platform) =>
    visible.has(platform)
  );
};

export const getPlatformApiPath = (platform) =>
  mapPlatformToPath(getResourceRoutePlatform(platform));

export const getSearchApiKey = (platform) => {
  if (isNativeResourcePlatform(platform)) return 'nativePlatformsUsers';
  if (['news', 'newspaper'].includes(platform)) return `${platform}Agencies`;
  return `${platform}Users`;
};

const RESOURCE_EXPORT_API_PATH = {
  linkedin: '/linkedin/users',
  virasty: '/virasty/users',
};

const getResourceExportApiPath = (platform) =>
  RESOURCE_EXPORT_API_PATH[platform];

/**
 * Params for one resource excel export. `api_path` is the backend platform
 * field. Native tabs share one path and add `platforms`.
 */
export const buildResourceExportParams = (platform, extra = {}) => {
  const api = apis(getSearchApiKey(platform), {});
  const nativePlatforms = getNativeSearchPlatforms(platform);
  const params = {
    ...(api?.params || {}),
    ...extra,
    api_path: api?.params?.api_path || getResourceExportApiPath(platform),
  };

  if (nativePlatforms) params.platforms = nativePlatforms;
  else delete params.platforms;

  return {
    params,
    exportPlatform: getResourceRoutePlatform(platform),
  };
};

export const isAgencyPlatform = (platform) =>
  ['news', 'newspaper'].includes(platform);

/**
 * The search box accepts an id/username or a profile link. Links are reduced to
 * the username segment (or the host for news agencies) before hitting the API.
 */
export const extractSearchTerm = (text, platform) => {
  const value = text.trim();
  if (isAgencyPlatform(platform)) return removeUrlProtocolParts(value);

  const linkMatch = value.match(
    /^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}\/(.+)$/i
  );
  if (!linkMatch) return value.replace(/^@/, '');

  const segments = linkMatch[1]
    .split(/[/?#]/)
    .filter(Boolean)
    .map((segment) => segment.replace(/^@/, ''));
  if (platform === 'telegram' && segments[0] === 's') return segments[1];
  return segments[0];
};

export const getResourceId = (item) => item.id ?? item.channel_id;

/**
 * VIP list rows keep a record id (for DELETE) and a resource id (for matching
 * a search result). Search rows only have the resource id.
 */
export const getVipRecordId = (item) => item.vipId ?? item.id;

export const getVipResourceId = (item) =>
  item.resource_id ?? item.resource?.id ?? getResourceId(item);

export { adaptVipResource as mapVipItemToSource } from 'store/vipResources/vipResources.adapter';

export const getResourceTitle = (item) =>
  item.title ?? item.name ?? item.fullName;

export const getResourceUsername = (item, platform) =>
  isAgencyPlatform(platform) ? item.url : item.username ?? item.screen_name;

const normalizeUsername = (value) =>
  String(value || '')
    .replace(/^@/, '')
    .toLowerCase();

export const isSameSource = (left, right, platform) => {
  const leftId = getResourceId(left);
  const rightId = getResourceId(right);
  if (leftId && rightId && String(leftId) === String(rightId)) return true;
  const leftName = normalizeUsername(getResourceUsername(left, platform));
  const rightName = normalizeUsername(getResourceUsername(right, platform));
  return Boolean(leftName && leftName === rightName);
};

export const isSameVipSource = (vipItem, searchItem, platform) => {
  const vipResourceId = getVipResourceId(vipItem);
  const searchId = getResourceId(searchItem);
  if (vipResourceId && searchId && String(vipResourceId) === String(searchId)) {
    return true;
  }
  return isSameSource(vipItem, searchItem, platform);
};

const SORT_FIELD_TO_METRIC = {
  follower_count: 'followerCount',
  post_count: 'publishRate',
  member_count: 'memberCount',
};

/**
 * One sortable metric next to influence. The metric follows the sort
 * dropdown; with no sort it stays on the platform default.
 */
export const getMetricsSyncedWithSort = (
  platform,
  sortField,
  metrics = RESOURCE_PLATFORM_METRICS
) => {
  const defaults = metrics[platform] || [];
  const sortMetric = SORT_FIELD_TO_METRIC[sortField];
  const fallback = defaults.find((key) => key !== 'influence');
  const metric = sortMetric || fallback;
  return metric ? [metric, 'influence'] : defaults;
};

const SORT_EXCLUDED_FIELDS = {
  telegram: ['post_count'],
  virasty: ['post_count'],
};

export const getResourceSortOptions = (sortOptions, platform) =>
  sortOptions.filter(
    ({ value }) => !SORT_EXCLUDED_FIELDS[platform]?.includes(value)
  );

export const getResourceMetrics = (item, platform, metricKeys) =>
  (metricKeys || RESOURCE_PLATFORM_METRICS[platform] || []).map(
    (metricKey) => ({
      key: metricKey,
      ...RESOURCE_METRICS[metricKey],
      value: RESOURCE_METRICS[metricKey].getValue(item, platform),
    })
  );

/**
 * Builds the URL of the source on its own platform (used by "link to source").
 */
export const getResourceExternalLink = (item, platform) => {
  const username = getResourceUsername(item, platform);
  switch (platform) {
    case 'telegram':
      return `https://t.me/${username}`;
    case 'instagram':
      return `https://www.instagram.com/${username}`;
    case 'twitter':
      return `https://twitter.com/${username}`;
    case 'news':
    case 'newspaper':
      return `https://${username}`;
    default:
      if (
        isNativeResourcePlatform(platform) ||
        platform === 'nativePlatforms'
      ) {
        const host =
          NATIVE_PLATFORM_LINKS[item.platform] ||
          NATIVE_PLATFORM_LINKS[`${platform}_channel`];
        return host ? `${host}/${username}` : undefined;
      }
      return undefined;
  }
};

export const getResourceProfilePath = (item, platform) =>
  `/resources/${getResourceRoutePlatform(platform)}/${getResourceId(
    item
  )}?from=resource`;

export const getResourceContentsPath = (item, platform) =>
  `/contents/${getResourceRoutePlatform(platform)}?resource_id=${getResourceId(
    item
  )}&resource_name=${getResourceTitle(item)?.replaceAll(
    ' ',
    '_'
  )}&from=resource`;
